import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';

// Shared card primitives used by the homepage sections and every dedicated
// page, so the whole site keeps one visual language. Use these instead of
// re-styling per page.
//
// The QAS logo is tricolor (petrol leaf, green A, red S): icon chips rotate
// through the three brand accents via the `accent` prop / accentAt() helper.

export const ACCENTS = {
  petrol: { fg: '#00425c', bg: 'rgba(0,66,92,0.09)' },
  green: { fg: '#6d8511', bg: 'rgba(134,163,21,0.14)' },
  red: { fg: '#c10a27', bg: 'rgba(193,10,39,0.09)' },
};

const ACCENT_ORDER = ['petrol', 'green', 'red'];
export const accentAt = (i) => ACCENT_ORDER[i % ACCENT_ORDER.length];

export const FeatureCard = styled(Box)(({ theme }) => ({
  backgroundColor: '#ffffff',
  border: '1px solid rgba(0,66,92,0.10)',
  borderRadius: '20px',
  padding: '32px 28px',
  height: '100%',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    transform: 'translateY(-6px)',
    boxShadow: '0 18px 40px rgba(0,66,92,0.12)',
    borderColor: 'transparent',
  },
}));

export const FeatureIcon = styled(Box, {
  shouldForwardProp: (p) => p !== 'accent',
})(({ theme, accent = 'petrol' }) => ({
  width: '54px',
  height: '54px',
  borderRadius: '16px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: (ACCENTS[accent] || ACCENTS.petrol).bg,
  color: (ACCENTS[accent] || ACCENTS.petrol).fg,
  marginBottom: '20px',
  '& svg': { fontSize: '28px' },
}));

export const CardTitle = styled(Typography)(({ theme }) => ({
  fontFamily: '"Sora", sans-serif',
  fontSize: '17.5px',
  fontWeight: 700,
  marginBottom: '10px',
  color: '#00425c',
}));

export const CardText = styled(Typography)(({ theme }) => ({
  fontSize: '14.5px',
  lineHeight: 1.75,
  color: '#48606d',
}));

// Dark-section variant (used on petrol sections).
export const DarkCard = styled(Box)(({ theme }) => ({
  backgroundColor: 'rgba(255,255,255,0.05)',
  border: '1px solid rgba(255,255,255,0.09)',
  borderRadius: '20px',
  padding: '32px 28px',
  height: '100%',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    transform: 'translateY(-6px)',
    backgroundColor: 'rgba(255,255,255,0.08)',
    boxShadow: '0 14px 32px rgba(0,0,0,0.28)',
  },
}));

export const Lead = styled(Typography)(({ theme }) => ({
  fontSize: '18px',
  lineHeight: 1.8,
  color: '#33454f',
  fontWeight: 500,
  marginBottom: '20px',
  [theme.breakpoints.down('sm')]: {
    fontSize: '16px',
  },
}));

// Compact ✓ item for capability grids.
export function CheckItem({ children }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.25,
        backgroundColor: '#ffffff',
        border: '1px solid rgba(0,66,92,0.10)',
        borderRadius: '14px',
        px: '18px',
        py: '16px',
        height: '100%',
        transition: 'all 0.25s ease',
        '&:hover': {
          borderColor: 'rgba(134,163,21,0.5)',
          boxShadow: '0 8px 22px rgba(0,66,92,0.08)',
        },
      }}
    >
      <CheckCircleRoundedIcon
        sx={{ fontSize: '20px', color: '#86a315', flexShrink: 0, mt: '2px' }}
      />
      <Typography sx={{ fontSize: '14.5px', lineHeight: 1.6, color: '#33454f', fontWeight: 600 }}>
        {children}
      </Typography>
    </Box>
  );
}

// The one sentence that backs every service claim on the site. Single source
// of truth for the wording.
export const ACCREDITATION_NOTE =
  'QAS Consulting è Centro di Formazione accreditato AiFOS e UNILAVORO P.M.I., certificato UNI EN ISO 9001:2015 e ISO 21001:2019 (ex UNI ISO 29990) per i servizi di formazione.';

export function AccreditationNote({ dark }) {
  return (
    <Box
      sx={{
        maxWidth: '860px',
        mx: 'auto',
        mt: 6,
        px: '24px',
        py: '18px',
        borderRadius: '14px',
        border: dark ? '1px solid rgba(255,255,255,0.14)' : '1px solid rgba(0,66,92,0.12)',
        backgroundColor: dark ? 'rgba(255,255,255,0.04)' : '#e9f1f6',
      }}
    >
      <Typography
        sx={{
          fontSize: '13.5px',
          lineHeight: 1.7,
          color: dark ? 'rgba(255,255,255,0.72)' : '#48606d',
          textAlign: 'center',
        }}
      >
        {ACCREDITATION_NOTE}
      </Typography>
    </Box>
  );
}
